import { styled } from "@mui/material/styles";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Radio } from "./Controls";
import { ChevronRightIcon, DocumentIcon, FolderIcon } from "./icons";
import { GroupedList, ListRow, SectionLabel } from "./Surfaces";
import { reducedMotion } from "./tokens";
const hasChildren = (item)=>!!item.children && item.children.length > 0;
function buildIndex(items) {
    const index = {
        byId: new Map(),
        parentOf: new Map(),
        descendantsOf: new Map(),
        leavesOf: new Map()
    };
    const visit = (item, parentId)=>{
        index.byId.set(item.itemId, item);
        if (parentId) index.parentOf.set(item.itemId, parentId);
        const descendants = [];
        const leaves = [];
        (item.children ?? []).forEach((child)=>{
            const result = visit(child, item.itemId);
            descendants.push(child.itemId, ...result.descendants);
            leaves.push(...hasChildren(child) ? result.leaves : [
                child.itemId
            ]);
        });
        index.descendantsOf.set(item.itemId, descendants);
        index.leavesOf.set(item.itemId, leaves);
        return {
            descendants,
            leaves
        };
    };
    items.forEach((item)=>visit(item));
    return index;
}
export function getTopSelectedItems(items, selectedIds) {
    const selected = new Set(selectedIds);
    const result = [];
    const visit = (item)=>{
        if (selected.has(item.itemId)) result.push(item);
        else (item.children ?? []).forEach((child)=>visit(child));
    };
    items.forEach(visit);
    return result;
}
const Root = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-3)",
    minWidth: 0
});
const Group = GroupedList;
const SectionHeading = SectionLabel;
const Chevron = styled("span")({
    display: "grid",
    placeItems: "center",
    color: "var(--j-ink-3)",
    transition: "transform var(--j-dur-fade) var(--j-ease)",
    "&[data-expanded=true]": {
        transform: "rotate(90deg)"
    },
    [reducedMotion]: {
        transition: "none"
    }
});
const IconSlot = styled("span")({
    display: "grid",
    placeItems: "center",
    color: "var(--j-ink-2)",
    "[data-selected=true] > &": {
        color: "var(--j-accent-ink)"
    }
});
const ControlSlot = styled("span")({
    display: "grid",
    placeItems: "center",
    padding: "var(--j-sp-1)",
    margin: "calc(-1 * var(--j-sp-1))"
});
const INDENT_BASE = "var(--j-sp-2)";
const INDENT_STEP = "1.75rem";
export function TreeList(props) {
    const { items, getItemIcon, getItemMeta, isItemDisabled, showRootsAsSections = false, showCounts = true, className, "aria-label": ariaLabel } = props;
    const { t } = useTranslation();
    const isMulti = props.selectionMode === "multiple";
    const index = useMemo(()=>buildIndex(items), [
        items
    ]);
    const [innerExpanded, setInnerExpanded] = useState(props.defaultExpandedItems ?? []);
    const expanded = useMemo(()=>new Set(props.expandedItems ?? innerExpanded), [
        props.expandedItems,
        innerExpanded
    ]);
    const selected = useMemo(()=>new Set(props.selectionMode === "multiple" ? props.selectedItems : props.selectedItems === null ? [] : [
            props.selectedItems
        ]), [
        props.selectedItems,
        props.selectionMode
    ]);
    const isSelectable = (item)=>props.isItemSelectable ? props.isItemSelectable(item) : isMulti || !hasChildren(item);
    const setExpanded = (next)=>{
        if (!props.expandedItems) setInnerExpanded(next);
        props.onExpandedItemsChange?.(next);
    };
    const toggleExpanded = (itemId, isOpen = !expanded.has(itemId))=>{
        if (isOpen === expanded.has(itemId)) return;
        setExpanded(isOpen ? [
            ...expanded,
            itemId
        ] : [
            ...expanded
        ].filter((id)=>id !== itemId));
    };
    const ancestorsOf = (itemId)=>{
        const ancestors = [];
        let current = index.parentOf.get(itemId);
        while(current){
            ancestors.push(current);
            current = index.parentOf.get(current);
        }
        return ancestors;
    };
    const toggleSelected = (item)=>{
        if (!isSelectable(item) || isItemDisabled?.(item)) return;
        if (props.selectionMode === "single") {
            props.onSelectedItemsChange(item.itemId, item);
            return;
        }
        const next = new Set(props.selectedItems);
        const affected = [
            item.itemId,
            ...index.descendantsOf.get(item.itemId) ?? []
        ];
        if (next.has(item.itemId)) {
            [
                ...affected,
                ...ancestorsOf(item.itemId)
            ].forEach((id)=>next.delete(id));
        } else {
            affected.forEach((id)=>next.add(id));
        }
        props.onSelectedItemsChange([
            ...next
        ], item);
    };
    const groups = useMemo(()=>{
        const flatten = (list, depth, out)=>{
            list.forEach((item)=>{
                out.push({
                    item,
                    depth
                });
                if (hasChildren(item) && expanded.has(item.itemId)) flatten(item.children, depth + 1, out);
            });
            return out;
        };
        if (!showRootsAsSections) return [
            {
                section: null,
                rows: flatten(items, 0, [])
            }
        ];
        return items.filter(hasChildren).map((section)=>({
                section,
                rows: flatten(section.children, 0, [])
            }));
    }, [
        items,
        expanded,
        showRootsAsSections
    ]);
    const visibleRows = useMemo(()=>groups.flatMap((group)=>group.rows), [
        groups
    ]);
    const rowRefs = useRef(new Map());
    const [focusedId, setFocusedId] = useState(null);
    const tabStopId = focusedId && visibleRows.some((row)=>row.item.itemId === focusedId) ? focusedId : visibleRows[0]?.item.itemId ?? null;
    const focusRow = (itemId)=>{
        if (!itemId) return;
        setFocusedId(itemId);
        rowRefs.current.get(itemId)?.focus();
    };
    const handleKeyDown = (event)=>{
        const position = visibleRows.findIndex((row)=>row.item.itemId === tabStopId);
        const row = visibleRows[position];
        if (!row) return;
        const { item } = row;
        const isParent = hasChildren(item);
        const isOpen = expanded.has(item.itemId);
        const handlers = {
            ArrowDown: ()=>focusRow(visibleRows[position + 1]?.item.itemId),
            ArrowUp: ()=>focusRow(visibleRows[position - 1]?.item.itemId),
            Home: ()=>focusRow(visibleRows[0]?.item.itemId),
            End: ()=>focusRow(visibleRows[visibleRows.length - 1]?.item.itemId),
            ArrowRight: ()=>{
                if (!isParent) return;
                if (isOpen) focusRow(item.children?.[0]?.itemId);
                else toggleExpanded(item.itemId, true);
            },
            ArrowLeft: ()=>{
                if (isParent && isOpen) toggleExpanded(item.itemId, false);
                else focusRow(index.parentOf.get(item.itemId));
            },
            Enter: ()=>isParent ? toggleExpanded(item.itemId) : toggleSelected(item),
            " ": ()=>isSelectable(item) ? toggleSelected(item) : toggleExpanded(item.itemId)
        };
        const handler = handlers[event.key];
        if (!handler) return;
        event.preventDefault();
        handler();
    };
    const renderCount = (item)=>{
        const leaves = index.leavesOf.get(item.itemId) ?? [];
        const selectedCount = isMulti ? leaves.filter((id)=>selected.has(id)).length : 0;
        return selectedCount > 0 ? t("ui.tree.countOf", {
            selected: selectedCount,
            total: leaves.length
        }) : leaves.length;
    };
    const renderRow = ({ item, depth })=>{
        const isParent = hasChildren(item);
        const isOpen = isParent && expanded.has(item.itemId);
        const isSelected = selected.has(item.itemId);
        const isDisabled = isItemDisabled?.(item) ?? false;
        const canSelect = isSelectable(item);
        const descendants = index.descendantsOf.get(item.itemId) ?? [];
        const isIndeterminate = isMulti && !isSelected && descendants.some((id)=>selected.has(id));
        const state = {
            isParent,
            isExpanded: isOpen,
            isSelected
        };
        const control = canSelect && (isMulti ? /*#__PURE__*/ React.createElement(Checkbox, {
            checked: isSelected,
            indeterminate: isIndeterminate,
            disabled: isDisabled
        }) : /*#__PURE__*/ React.createElement(Radio, {
            checked: isSelected,
            disabled: isDisabled
        }));
        const trailing = /*#__PURE__*/ React.createElement(React.Fragment, null, showCounts && isParent && /*#__PURE__*/ React.createElement("span", {
            className: "row-meta"
        }, renderCount(item)), control && /*#__PURE__*/ React.createElement(ControlSlot, {
            onClick: (event)=>{
                event.stopPropagation();
                toggleSelected(item);
            }
        }, control));
        return /*#__PURE__*/ React.createElement(ListRow, {
            key: item.itemId,
            as: "div",
            ref: (element)=>{
                if (element) rowRefs.current.set(item.itemId, element);
                else rowRefs.current.delete(item.itemId);
            },
            role: "treeitem",
            "aria-level": depth + 1,
            "aria-expanded": isParent ? isOpen : undefined,
            "aria-selected": !isMulti && canSelect ? isSelected : undefined,
            "aria-checked": isMulti && canSelect ? isIndeterminate ? "mixed" : isSelected : undefined,
            tabIndex: item.itemId === tabStopId ? 0 : -1,
            dense: true,
            disabled: isDisabled,
            selected: !isMulti && isSelected,
            style: {
                paddingLeft: `calc(${INDENT_BASE} + ${depth} * ${INDENT_STEP})`
            },
            titleWeight: isParent ? 500 : undefined,
            title: item.title,
            meta: getItemMeta?.(item),
            onFocus: ()=>setFocusedId(item.itemId),
            onClick: ()=>isParent ? toggleExpanded(item.itemId) : toggleSelected(item),
            leading: /*#__PURE__*/ React.createElement(React.Fragment, null, isParent && /*#__PURE__*/ React.createElement(Chevron, {
                "data-expanded": isOpen
            }, /*#__PURE__*/ React.createElement(ChevronRightIcon, {
                size: 14
            })), /*#__PURE__*/ React.createElement(IconSlot, {
                "data-parent": isParent
            }, getItemIcon?.(item, state) ?? (isParent ? /*#__PURE__*/ React.createElement(FolderIcon, null) : /*#__PURE__*/ React.createElement(DocumentIcon, {
                size: 14
            })))),
            trailing: trailing
        });
    };
    return /*#__PURE__*/ React.createElement(Root, {
        role: "tree",
        "aria-label": ariaLabel,
        "aria-multiselectable": isMulti,
        className: className,
        onKeyDown: handleKeyDown
    }, groups.map(({ section, rows })=>/*#__PURE__*/ React.createElement(React.Fragment, {
            key: section?.itemId ?? "root"
        }, section && /*#__PURE__*/ React.createElement(SectionHeading, {
            "aria-hidden": true
        }, section.title), /*#__PURE__*/ React.createElement(Group, {
            role: "group",
            "aria-label": section?.title
        }, rows.map(renderRow)))));
}
